/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: event_statuses entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the event_statuses table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the event_statuses API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, name, description, created_by, created_at, updated_by, updated_at]
 * Searchable fields: []
 * Filterable fields: [id]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  EventStatusesRow,
  EventStatusesInsert,
  EventStatusesUpdate
} from '../../types/generated_schemas/generated-event_statuses-schema.js';

export interface EventStatusesQueryOptions extends UnifiedQueryOptions {}

export interface EventStatusesResponse {
  data: EventStatusesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for event_statuses table operations
 *
 * @description Provides type-safe access to event_statuses API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class EventStatusesClient {
  private readonly basePath = '/event-statuses';

  /**
   * Get all event_statuses records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of event_statuses records
   */
  async getAll(options?: EventStatusesQueryOptions): Promise<EventStatusesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<EventStatusesResponse>(url);
    return response.data;
  }

  /**
   * Get a single event_statuses record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the event_statuses record
   */
  async getById(id: string): Promise<EventStatusesRow> {
    const response = await httpClient.get<{
      data: EventStatusesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new event_statuses record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created event_statuses record
   */
  async create(data: EventStatusesInsert): Promise<EventStatusesRow> {
    const response = await httpClient.post<{
      data: EventStatusesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing event_statuses record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated event_statuses record
   */
  async update(id: string, data: EventStatusesUpdate): Promise<EventStatusesRow> {
    const response = await httpClient.put<{
      data: EventStatusesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a event_statuses record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple event_statuses records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created event_statuses records
   */
  async createMany(data: EventStatusesInsert[]): Promise<EventStatusesRow[]> {
    const response = await httpClient.post<{
      data: EventStatusesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple event_statuses records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search event_statuses with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: EventStatusesQueryOptions
  ): Promise<EventStatusesResponse> {
    throw new Error('Search operations are not available for event_statuses - no searchable fields configured');
  }

  /**
   * Get count of event_statuses records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a event_statuses record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'event_statuses',
      publicFields: ['id', 'name', 'description', 'created_by', 'created_at', 'updated_by', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'name'],
    };
  }
}

// Export singleton instance for convenience
export const eventStatusesClient = new EventStatusesClient();
